import { NavigateFunction } from 'react-router';
import { toolRedirect } from '../../../services/http';
import { FetchFlipperResponseType } from '../../../services/merchants';
import { CompanyType, MerchantType } from '../../../types/dataTypes';
import { AddProductOptionsType, getPath } from '../../MyMerchantView/helpers';
import {
  CLEVERTAP_ADDITIONAL,
  CLEVERTAP_SCREEN_NAME,
  CLEVERTAP_SECTION_NAME,
  CLEVERTAP_TAPPED_ON,
} from '../../../helpers/ClevertapKeys';
import { ModalFunctions, Select } from 'fave-ui';
import useCleverTap from '../../../hooks/useCleverTap';
import style from './style.module.css';
import { FetchMcProductListResponse } from '../../../services/MySubmissions';

type CleverTap = ReturnType<typeof useCleverTap>;

export const addNewMerchantRedirect = (type: MerchantType) =>
  toolRedirect(
    `/admin/merchant_connect/merchants/new`,
    `merchant_type=${type}`,
  );

export const addProductsModal = ({
  cleverTap,
  companyData,
  stampCardDraftData,
  enableIppOnboarding,
  isFavePaymentModeEnabled,
  isFavePassOnboardingDisabled,
  isStampCardOnboardingDisabled,
  isUobPointRedemptionOnboardingDisabled,
  navigateFn,
}: {
  cleverTap: CleverTap;
  companyData: CompanyType;
  stampCardDraftData?: FetchMcProductListResponse;
  enableIppOnboarding: boolean;
  isFavePaymentModeEnabled: FetchFlipperResponseType | undefined;
  isFavePassOnboardingDisabled: FetchFlipperResponseType | undefined;
  isStampCardOnboardingDisabled: FetchFlipperResponseType | undefined;
  isUobPointRedemptionOnboardingDisabled: FetchFlipperResponseType | undefined;
  navigateFn: NavigateFunction;
}) => {
  let selectedProduct: Pick<AddProductOptionsType, 'value' | 'redirect'>;

  const draftStampCardData =
    stampCardDraftData &&
    stampCardDraftData.mc_products &&
    stampCardDraftData.mc_products[0];

  const hasDraftStampCard = !!draftStampCardData;

  cleverTap.tapped.push({
    screen_name: CLEVERTAP_SCREEN_NAME.MY_MERCHANT,
    section_name: CLEVERTAP_SECTION_NAME.ACTIONS_MENU,
    tapped_on: CLEVERTAP_TAPPED_ON.BUTTON_ADD_NEW_PRODUCT,
    platform: CLEVERTAP_ADDITIONAL.MERCHANT_CONNECT,
  });

  const productOptions =
    companyData.merchant_type === 'fastpay'
      ? [
          {
            label: 'FastPay / Payment Mode(s)',
            key: '1',
            value: `${
              companyData.id
                ? `/${companyData.id}/favepay${companyData.has_draft_payment_mode ? '' : '/new'}`
                : '/add_fastpay'
            }`,
            redirect: true,
            show: true,
          },
          {
            label: 'Instalment payment plan (IPP)',
            key: '2',
            value: `/admin/merchant_connect/my_merchant/merchants/${companyData.id}/ipp/${
              companyData.has_draft_ipp ? 'edit' : 'new'
            }`,
            redirect: false,
            show: enableIppOnboarding,
          },
        ].filter(item => item.show)
      : [
          {
            label: 'FavePay / Alipay',
            key: '1',
            value: `${companyData.id ? `/${companyData.id}/favepay/new` : '/add_favepay'}`,
            redirect: true,
            show: true,
          },
          {
            label: 'Payment Mode(s)',
            key: '2',
            value: `/admin/merchant_connect/merchants/${companyData.id}/payment_mode/resubmit`,
            redirect: false,
            show: isFavePaymentModeEnabled?.value,
          },
          {
            label: 'Deals',
            key: '3',
            value: `${companyData.id ? `/${companyData.id}/deals/new` : '/add_deal'}`,
            redirect: true,
            show: true,
          },
          {
            label: 'eCards (FavePay required)',
            key: '4',
            value: `${companyData.id ? `/${companyData.id}/ecards/new` : '/add_ecard'}`,
            redirect: true,
            show: true,
          },
          {
            label: 'Fave Passes',
            key: '6',
            value: `/admin/merchant_connect/merchants/${companyData.id}/fave_passes/new`,
            redirect: false,
            show: !isFavePassOnboardingDisabled?.value,
          },
          {
            label: 'Stamp Card',
            key: '7',
            value: `/admin/merchant_connect/merchants/${companyData.id}/loyalty_settings/${hasDraftStampCard ? `${draftStampCardData.id}/edit` : 'new'}`,
            redirect: false,
            show: !isStampCardOnboardingDisabled?.value,
          },
          {
            label: 'UOB Point Redemption (POS Terminal)',
            key: '8',
            value: `/admin/merchant_connect/merchants/${companyData.id}/uob_point_redemptions/new`,
            redirect: false,
            show: !isUobPointRedemptionOnboardingDisabled?.value,
          },
        ].filter(item => item.show);

  const modalContent = (
    <div>
      <span>
        {`Please select the product you would like to add to ${companyData.name}.`}
      </span>
      <Select
        className={style.addNewProductModal}
        onChange={(value, option) => {
          const optionValue = option as AddProductOptionsType;
          selectedProduct = {
            value: optionValue.redirect ? getPath(value) : value,
            redirect: optionValue.redirect,
          };
        }}
        placeholder={'Select a product'}
        showArrow
        options={productOptions}
      />
    </div>
  );

  ModalFunctions.confirm({
    title: 'Select a product',
    content: modalContent,
    okText: 'Confirm',
    cancelText: 'Cancel',
    centered: true,
    onOk: () =>
      selectedProduct.redirect
        ? toolRedirect(selectedProduct.value)
        : navigateFn(selectedProduct.value),
  });
};
